// Bridges Auth0 (a React context) to the plain API module, so every backend call can attach the logged-in user's
// access token.

/** Auth0 tenant (custom domain) and this web app's client ID. Public values. */
export const AUTH0_DOMAIN = 'auth0.leftturnbetting.com';
export const AUTH0_CLIENT_ID = '6bgfu6i3ZGx41R4SL6GQxGnjcDVzxEec';

/** Auth0 API identifier ("audience"). When unset, the app keeps using the legacy shared API key. */
export const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || '';

type TokenGetter = () => Promise<string>;

let tokenGetter: TokenGetter | null = null;
let loginRedirect: (() => void) | null = null;
let redirecting = false;

// Errors that a fresh interactive login fixes: sessions from before the API audience / refresh tokens were enabled,
// consent not yet given (always required on localhost), or an expired session.
const RECOVERABLE_ERRORS = new Set(['login_required', 'consent_required', 'interaction_required', 'missing_refresh_token', 'invalid_grant']);

export const setAccessTokenGetter = (getter: TokenGetter | null): void => {
  tokenGetter = getter;
};

/** Registers the Auth0 login redirect, used once to recover when a silent token request can't succeed. */
export const setLoginRedirect = (redirect: (() => void) | null): void => {
  loginRedirect = redirect;
};

/** The current user's access token, or null if not logged in / token auth not configured / token unavailable. */
export const getAccessToken = async (): Promise<string | null> => {
  if (!tokenGetter) return null;
  try {
    return await tokenGetter();
  } catch (err) {
    console.warn('Could not get an Auth0 access token', err);
    const code = (err as { error?: string } | null)?.error;
    if (code && RECOVERABLE_ERRORS.has(code) && loginRedirect && !redirecting) {
      // Log in again (at most once per page load) to get consent and a refresh token for the API.
      redirecting = true;
      loginRedirect();
    }
    return null;
  }
};

/** Whether a signed-in user's token getter is registered (no token request is made). */
export const hasSignedInUser = (): boolean => tokenGetter !== null;
